import React from 'react';
import PropTypes from 'prop-types';
import { Label } from '@fluentui/react-components';
import {
  getPanelErrorProps,
  TextTitle,
  ValidatedPanel,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-message-bar';
import { orComp, propertyIs } from '@bingads-webui-campaign-react/form-error';
import { DynamicSearchAd } from '@bingads-webui/campaign-odata-schemas';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { ValidatedTextFieldWithAdCustomizer } from '@bingads-webui-campaign-react/responsive-search-ad-editor';
import { ValidatedUrlPath } from '@bingads-webui-campaign-react/validated-url-input';
import { withError } from '@bingads-webui-react/validation';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { useAdCustomizer } from './use-ad-customizer';
import AdTextImg from '../media/ad-text.svg';

const pathProps = orComp(propertyIs('Path1'), propertyIs('Path2'));
const ServerValidatedUrlPath = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedUrlPath, { filter: pathProps }));

const AdTextInternal = ({
  i18n,
  expand,
  data,
  domain,
  permissions,
  scope,
  ...rest
}) => {
  const {
    Text,
    TextPart2,
    DisplayUrl,
    Path1,
    Path2,
  } = data;

  const { campaignId, adGroupId } = scope;

  useAdCustomizer({
    campaignId,
    adGroupId,
    i18n,
    permissions,
  });

  return (
    <ValidatedPanel
      key="ad-text"
      title={<TextTitle titleKey={_TL_('Ad text')} topicKey="pop_TBD" icon={AdTextImg} />}
      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      actuationProps={{
        expand,
        expandKey: 'ad-text',
      }}
      {...rest}
    >
      <div className="ad-text-panel">
        <div className="final-url">
          <div className="field-label">
            <Label weight="semibold">
              {i18n.getString(_TL_('Final URL'))}
            </Label>
            <HelpPopup
              topic="pop_BA_NewAdCreate_FinalURL"
              aria-label={i18n.getString(_TL_('Click to learn more about Final URL'))}
            />
          </div>
          <div className="field-value">
            {i18n.getString(_TL_('Dynamically selected landing page'))}
          </div>
        </div>
        <div className="ad-title">
          <div className="field-label">
            <Label weight="semibold">
              {i18n.getString(_TL_('Ad title'))}
            </Label>
            <HelpPopup
              topic="pop_BA_CampaignCreate_Ads_AdTitle"
              aria-label={i18n.getString(_TL_('Click to learn more about Ad title'))}
            />
          </div>
          <div className="field-value">
            {i18n.getString(_TL_('Dynamically generated headline'))}
          </div>
        </div>
        <div className="ad-text">
          <div className="field-label">
            <Label weight="semibold">
              {i18n.getString(_TL_('Ad text 1'))}
            </Label>
            <HelpPopup
              topic="pop_BA_NewAdCreate_AdText"
              aria-label={i18n.getString(_TL_('Click to learn more about Ad text 1'))}
            />
          </div>
          <div className="field-value">
            <ValidatedTextFieldWithAdCustomizer
              className="input-ad-text"
              i18n={i18n}
              adType="DynamicSearchAd"
              containerSelector=".dynamic-search-ad-container"
              validationProps={{
                data,
                field: 'Text',
                schema: DynamicSearchAd,
              }}
              configProps={{
                id: 'Text',
                key: 'Descriptions',
                placeholder: i18n.getString(_TL_('Enter ad text')),
                title: Text,
              }}
            />
          </div>
        </div>
        <div className="ad-text-2">
          <div className="field-label">
            <Label weight="semibold">
              {i18n.getString(_TL_('Ad text 2'))}
            </Label>
            <HelpPopup
              topic="pop_BA_NewAdCreate_AdText2"
              aria-label={i18n.getString(_TL_('Click to learn more about Ad text 2'))}
            />
          </div>
          <div className="field-value">
            <ValidatedTextFieldWithAdCustomizer
              className="input-ad-text-2"
              i18n={i18n}
              adType="DynamicSearchAd"
              containerSelector=".dynamic-search-ad-container"
              validationProps={{
                data,
                field: 'TextPart2',
                schema: DynamicSearchAd,
              }}
              configProps={{
                id: 'TextPart2',
                key: 'Descriptions',
                placeholder: i18n.getString(_TL_('Enter ad text')),
                title: TextPart2,
              }}
            />
          </div>
        </div>
        <div className="ad-text-disclaimer">
          <MessageBar className="message-bar">
            <MessageBarBody>
              <MessageBarTitle>{i18n.getString(_TL_('Note'))}</MessageBarTitle>
              <LearnMoreLink
                topic="app54695"
                text={i18n.getString(_TL_('In addition to your Ad texts, we will generate dynamic search ad text.'))}
                linkText={i18n.getString(_TL_('Learn more'))}
              />
            </MessageBarBody>
          </MessageBar>
        </div>
        <div className="ad-path">
          <div className="field-label">
            <Label weight="semibold">
              {i18n.getString(_TL_('Path'))}
            </Label>
            <HelpPopup
              topic="pop_BA_NewAdCreate_DSA_Path"
              aria-label={i18n.getString(_TL_('Click to learn more about Path'))}
            />
          </div>
          <div className="field-value">
            <ServerValidatedUrlPath
              i18n={i18n}
              validationProps={{ data: { Path1, Path2, FinalUrls: [DisplayUrl || domain] } }}
            />
          </div>
        </div>
      </div>
    </ValidatedPanel>
  );
};

AdTextInternal.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  data: PropTypes.shape({
    Text: PropTypes.string,
    TextPart2: PropTypes.string,
    DisplayUrl: PropTypes.string,
    Path1: PropTypes.string,
    Path2: PropTypes.string,
  }),
  permissions: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    campaignId: PropTypes.string,
    adGroupId: PropTypes.string,
  }).isRequired,
  domain: PropTypes.string,
  onDataChange: PropTypes.func.isRequired,
  expand: PropTypes.func.isRequired,
};

AdTextInternal.defaultProps = {
  data: {
    Text: '',
    TextPart2: '',
    DisplayUrl: '',
    Path1: '',
    Path2: '',
  },
  domain: '',
};

export const AdText = withControlErrorHandling(withUnvalidatedControlErrors(withError(AdTextInternal)));
